import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Select, DatePicker } from "antd";
import { ArrowLeftRight, RefreshCw } from "lucide-react";
import { DialogModal, ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { Textarea } from "../../../components/ui/input";
import { EmployeeDetails } from "../../../components/ui/cards";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../../components/common/ToastMsg";
import { SHIFT_CHANGE_REQUEST } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import { antPickerClass, antSelectClass, modalFieldLabelClass, modalTextareaClass } from "../../../components/ui/fieldStyles";
import {
    MAX_BACKDATE_DAYS,
    MAX_SWAP_AHEAD_DAYS,
    apiErrorMessage,
    buildShiftOptionLabel,
    buildSwapCandidateLabel,
    disableShiftChangeDate,
    disableSwapDate,
    toEmployeeDetails,
} from "./shiftChangeUtils";

const emptyForm = (date, requestType = "change") => ({
    request_type: requestType,
    date: date || dayjs().format("YYYY-MM-DD"),
    requested_shift_id: "",
    counterpart_id: "",
    reason: "",
});

const extractCurrentShift = (res) => {
    if (!res) return null;
    if (res.current_shift && typeof res.current_shift === "object") return res.current_shift;
    const nested = res.data && !Array.isArray(res.data) ? res.data.current_shift : null;
    return nested && typeof nested === "object" ? nested : null;
};

const candidateFromRecord = (record) => {
    if (!record?.counterpart_id) return null;
    return {
        id: record.counterpart_id,
        employee_id: record.counterpart_employee_id,
        employee_name: record.counterpart_name || "Selected colleague",
        employee_details: record.counterpart_details || null,
        shift_id: record.requested_shift_id,
        shift_name: record.requested_shift_name,
    };
};

const mergeById = (rows, extra) => {
    const list = Array.isArray(rows) ? [...rows] : [];
    if (!extra?.id) return list;
    if (list.some((row) => String(row.id) === String(extra.id))) return list;
    return [extra, ...list];
};

const TypeCard = ({ value, selected, onSelect, disabled, icon: Icon, title, description }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect(value)}
        className={cn(
            "w-full text-left rounded-xl border px-3 py-2 transition-colors",
            selected
                ? "border-sidebar-primary bg-sidebar-primary/5 ring-1 ring-sidebar-primary/30"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
            disabled && !selected && "opacity-50 cursor-not-allowed",
        )}
    >
        <div className="flex items-center gap-2.5">
            <span
                className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                    selected
                        ? "bg-sidebar-primary text-white"
                        : "bg-gray-100 text-muted-foreground dark:bg-gray-800",
                )}
            >
                <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{description}</span>
            </span>
        </div>
    </button>
);

const splitShiftLabel = (label) => {
    const text = String(label || "").trim();
    const match = text.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
    if (match) return { name: match[1], hint: match[2] };
    return { name: text, hint: null };
};

const ShiftLane = ({ label, name, hint, accent = false, empty = false }) => {
    const parsed = hint ? { name, hint } : splitShiftLabel(name);
    return (
        <div
            className={cn(
                "rounded-lg border px-3 py-2 min-w-0",
                accent
                    ? "border-sidebar-primary/30 bg-sidebar-primary/5"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800",
            )}
        >
            <p className={cn(
                "text-[10px] font-medium uppercase tracking-wide",
                accent ? "text-sidebar-primary" : "text-muted-foreground",
            )}>
                {label}
            </p>
            <p
                className={cn(
                    "mt-0.5 text-sm font-semibold leading-snug truncate",
                    empty ? "text-muted-foreground font-medium" : "text-gray-900 dark:text-gray-100",
                )}
                title={parsed.hint ? `${parsed.name} (${parsed.hint})` : parsed.name}
            >
                {parsed.name}
            </p>
            {parsed.hint ? (
                <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{parsed.hint}</p>
            ) : null}
        </div>
    );
};

const SwapPair = ({ title, fromLabel, fromName, toLabel, toName, ready }) => (
    <div className="min-w-0">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">{title}</p>
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
            <ShiftLane label={fromLabel} name={fromName} empty={!ready} />
            <ArrowLeftRight className="h-4 w-4 text-muted-foreground shrink-0" />
            <ShiftLane label={toLabel} name={toName} accent={ready} empty={!ready} />
        </div>
    </div>
);

const ShiftChangeRequestDialog = ({
    open,
    onClose,
    onSuccess,
    initialDate = null,
    lockDate = false,
    currentShiftLabel = null,
    hasAttendance = true,
    editRecord = null,
}) => {
    const { fetch, create, update } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [shifts, setShifts] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [requesterShiftName, setRequesterShiftName] = useState(currentShiftLabel || "");
    const [currentShiftDetails, setCurrentShiftDetails] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [form, setForm] = useState(emptyForm(initialDate));
    const candidatesReqId = useRef(0);

    const isSwap = form.request_type === "swap";
    const typeLocked = Boolean(editingId);

    const fallbackCandidate = useMemo(
        () => candidateFromRecord(editRecord),
        [editRecord],
    );

    const displayCandidates = useMemo(
        () => mergeById(candidates, fallbackCandidate),
        [candidates, fallbackCandidate],
    );

    const shiftOptions = useMemo(() => {
        const options = shifts.map((s) => ({ value: s.id, label: buildShiftOptionLabel(s) }));
        const selectedId = form.requested_shift_id;
        const selectedName = editRecord?.requested_shift_name;
        if (
            selectedId
            && selectedName
            && !options.some((opt) => String(opt.value) === String(selectedId))
        ) {
            options.unshift({ value: selectedId, label: selectedName });
        }
        return options;
    }, [shifts, form.requested_shift_id, editRecord]);

    const candidateOptions = useMemo(
        () => displayCandidates.map((person) => ({
            value: person.id,
            label: buildSwapCandidateLabel(person),
        })),
        [displayCandidates],
    );

    const selectedCandidate = useMemo(
        () => displayCandidates.find((person) => String(person.id) === String(form.counterpart_id)) || null,
        [displayCandidates, form.counterpart_id],
    );

    const selectedShift = useMemo(
        () => shifts.find((shift) => String(shift.id) === String(form.requested_shift_id)) || null,
        [shifts, form.requested_shift_id],
    );

    const applyCurrentShift = (shift) => {
        if (!shift || typeof shift !== "object") return;
        setCurrentShiftDetails(shift);
        const label = buildShiftOptionLabel(shift) || shift.shift_name || shift.name || "";
        if (label) setRequesterShiftName(label);
    };

    const yourShiftLabel = requesterShiftName
        || (currentShiftDetails ? buildShiftOptionLabel(currentShiftDetails) : "")
        || currentShiftLabel
        || editRecord?.current_shift_name
        || "Your assigned shift";
    const yourShiftParts = splitShiftLabel(yourShiftLabel);

    const resetState = useCallback((date, requestType = "change") => {
        candidatesReqId.current += 1;
        setEditingId(null);
        setCandidates([]);
        setForm(emptyForm(date || initialDate || dayjs().format("YYYY-MM-DD"), requestType));
    }, [initialDate]);

    const loadShifts = useCallback(async (dateStr) => {
        const filters = { shifts_only: true };
        if (dateStr) filters.date = dateStr;
        const res = await fetch(SHIFT_CHANGE_REQUEST, filters);
        if (res?.status !== "success") {
            setShifts([]);
            return;
        }
        const list = Array.isArray(res.data) ? res.data : (res?.data?.result || []);
        setShifts(Array.isArray(list) ? list : []);
        applyCurrentShift(extractCurrentShift(res));
    }, [fetch]);

    const loadCandidates = useCallback(async (dateStr, excludeRequestId = null) => {
        const reqId = ++candidatesReqId.current;
        if (!dateStr) {
            setCandidates([]);
            return;
        }
        setCandidates([]);
        const filters = {
            swap_candidates: true,
            date: dateStr,
        };
        if (excludeRequestId) filters.exclude_id = excludeRequestId;
        const res = await fetch(SHIFT_CHANGE_REQUEST, filters);
        if (reqId !== candidatesReqId.current) return;
        if (res?.status !== "success") {
            if (res?.message) ToastMsg("error", res.message);
            setCandidates([]);
            return;
        }
        setCandidates(Array.isArray(res.data) ? res.data : []);
        applyCurrentShift(extractCurrentShift(res));
    }, [fetch]);

    const loadPendingForDate = useCallback(async (dateStr) => {
        if (!dateStr) return null;
        const res = await fetch(SHIFT_CHANGE_REQUEST, {
            date: dateStr,
            status: "pending",
            mine_only: true,
        });
        if (res?.status !== "success") return null;
        const rows = Array.isArray(res.data) ? res.data : (res?.data?.result || []);
        return rows.find((row) => row.status === "pending" && row.my_role !== "counterpart") || null;
    }, [fetch]);

    useEffect(() => {
        if (!open) return;

        const dateStr = editRecord?.date || initialDate || dayjs().format("YYYY-MM-DD");

        (async () => {
            setLoading(true);
            try {
                await loadShifts(dateStr);
                if (editRecord) {
                    setEditingId(editRecord.id);
                    if (editRecord.current_shift_name || currentShiftLabel) {
                        setRequesterShiftName(editRecord.current_shift_name || currentShiftLabel);
                    }
                    setForm({
                        request_type: editRecord.request_type || "change",
                        date: editRecord.date || dateStr,
                        requested_shift_id: editRecord.requested_shift_id || "",
                        counterpart_id: editRecord.counterpart_id || "",
                        reason: editRecord.reason || "",
                    });
                    setCandidates(mergeById([], candidateFromRecord(editRecord)));
                    if ((editRecord.request_type || "change") === "swap") {
                        await loadCandidates(editRecord.date || dateStr, editRecord.id);
                    }
                    return;
                }

                resetState(dateStr, "change");
                await loadShifts(dateStr);
                const pending = await loadPendingForDate(dateStr);
                if (pending) {
                    setEditingId(pending.id);
                    if (pending.current_shift_name || currentShiftLabel) {
                        setRequesterShiftName(pending.current_shift_name || currentShiftLabel);
                    }
                    setForm({
                        request_type: pending.request_type || "change",
                        date: pending.date || dateStr,
                        requested_shift_id: pending.requested_shift_id || "",
                        counterpart_id: pending.counterpart_id || "",
                        reason: pending.reason || "",
                    });
                    setCandidates(mergeById([], candidateFromRecord(pending)));
                    if ((pending.request_type || "change") === "swap") {
                        await loadCandidates(pending.date || dateStr, pending.id);
                    }
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [open, initialDate, editRecord, currentShiftLabel, loadShifts, loadPendingForDate, loadCandidates, resetState]);

    useEffect(() => {
        if (!open || !isSwap || !form.date || editingId) return;
        loadCandidates(form.date);
    }, [open, isSwap, form.date, editingId, loadCandidates]);

    useEffect(() => {
        if (!open || editingId || !form.date) return;
        loadShifts(form.date);
    }, [open, editingId, form.date, loadShifts]);

    const handleClose = () => {
        setCancelOpen(false);
        onClose?.();
    };

    const setRequestType = (nextType) => {
        if (typeLocked) return;
        if (nextType === "swap" && form.date && dayjs(form.date).isBefore(dayjs().startOf("day"))) {
            ToastMsg("error", "A shift swap cannot be requested for a past date.");
            return;
        }
        setForm((prev) => ({
            ...prev,
            request_type: nextType,
            requested_shift_id: "",
            counterpart_id: "",
        }));
        if (nextType !== "swap") setCandidates([]);
    };

    const validateForm = () => {
        if (!form.date || !form.reason.trim()) {
            ToastMsg("error", isSwap ? "Date, colleague, and reason are required" : "Date, shift, and reason are required");
            return false;
        }
        if (isSwap && form.date && dayjs(form.date).isBefore(dayjs().startOf("day"))) {
            ToastMsg("error", "A shift swap cannot be requested for a past date.");
            return false;
        }
        if (isSwap && !form.counterpart_id) {
            ToastMsg("error", "Select the employee you want to swap with");
            return false;
        }
        if (!isSwap && !form.requested_shift_id) {
            ToastMsg("error", "Date, shift, and reason are required");
            return false;
        }
        if (form.reason.trim().length < 10) {
            ToastMsg("error", "Reason must be at least 10 characters");
            return false;
        }
        if (!isSwap && !hasAttendance) {
            ToastMsg("error", "Attendance must exist for this date before requesting a shift change");
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            const payload = editingId
                ? (isSwap
                    ? { id: editingId, reason: form.reason.trim() }
                    : {
                        id: editingId,
                        requested_shift_id: form.requested_shift_id,
                        reason: form.reason.trim(),
                    })
                : (isSwap
                    ? {
                        date: form.date,
                        request_type: "swap",
                        counterpart_id: form.counterpart_id,
                        reason: form.reason.trim(),
                    }
                    : {
                        date: form.date,
                        request_type: "change",
                        requested_shift_id: form.requested_shift_id,
                        reason: form.reason.trim(),
                    });

            const res = editingId
                ? await update(SHIFT_CHANGE_REQUEST, payload)
                : await create(SHIFT_CHANGE_REQUEST, payload);

            if (res?.status === "success") {
                ToastMsg("success", res.message || (editingId ? "Request updated" : "Request submitted"));
                handleClose();
                onSuccess?.();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to save request"));
            }
        } catch (err) {
            ToastMsg("error", apiErrorMessage(err, "Failed to save request"));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!editingId) return;
        try {
            setLoading(true);
            const res = await update(SHIFT_CHANGE_REQUEST, { id: editingId, status: "cancelled" });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Request cancelled");
                setCancelOpen(false);
                handleClose();
                onSuccess?.();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Failed to cancel request"));
            }
        } catch (err) {
            ToastMsg("error", apiErrorMessage(err, "Failed to cancel request"));
        } finally {
            setLoading(false);
        }
    };

    const dialogTitle = editingId
        ? (isSwap ? "Edit swap request" : "Edit shift change")
        : "New shift request";

    const theirShiftLabel = selectedCandidate
        ? (buildShiftOptionLabel(selectedCandidate) || selectedCandidate.shift_name || editRecord?.requested_shift_name)
        : (editRecord?.requested_shift_name || "Colleague's shift");
    const requestedShiftLabel = selectedShift
        ? buildShiftOptionLabel(selectedShift)
        : (editRecord?.requested_shift_name || "Select a shift");

    return (
        <>
            <DialogModal
                open={open}
                onClose={handleClose}
                onSave={handleSubmit}
                saveLabel={editingId ? "Update" : (isSwap ? "Send swap request" : "Submit")}
                loading={loading}
                title={dialogTitle}
                width="1080px"
                className="overflow-x-hidden [&>div:nth-child(2)]:mb-3"
                onReject={editingId ? () => setCancelOpen(true) : undefined}
                rejectLabel="Cancel this request"
            >
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <TypeCard
                            value="change"
                            selected={!isSwap}
                            onSelect={setRequestType}
                            disabled={typeLocked || loading}
                            icon={RefreshCw}
                            title="Change my shift"
                            description={`Attendance needed · today or ${MAX_BACKDATE_DAYS} days back`}
                        />
                        <TypeCard
                            value="swap"
                            selected={isSwap}
                            onSelect={setRequestType}
                            disabled={typeLocked || loading}
                            icon={ArrowLeftRight}
                            title="Swap with a colleague"
                            description={`They accept first · today or ${MAX_SWAP_AHEAD_DAYS} days ahead`}
                        />
                    </div>

                    {!isSwap && !hasAttendance && (
                        <p className="text-xs text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
                            No attendance record exists for this date. Shift change can only be requested when attendance is present.
                        </p>
                    )}

                    <div className="grid grid-cols-3 gap-3 items-start">
                        <div className="min-w-0">
                            <label className={modalFieldLabelClass}>Date</label>
                            <DatePicker
                                className={cn(antPickerClass(), "mt-1")}
                                format="DD-MM-YYYY"
                                value={form.date ? dayjs(form.date) : null}
                                onChange={(value) => {
                                    candidatesReqId.current += 1;
                                    setCandidates([]);
                                    setForm((f) => ({
                                        ...f,
                                        date: value ? value.format("YYYY-MM-DD") : "",
                                        counterpart_id: "",
                                    }));
                                }}
                                disabledDate={isSwap ? disableSwapDate : disableShiftChangeDate}
                                getPopupContainer={() => document.body}
                                allowClear={false}
                                disabled={lockDate || Boolean(editingId) || loading}
                            />
                            <p className="text-[11px] text-muted-foreground mt-1 leading-4 min-h-4">
                                {lockDate || editingId
                                    ? "Date is fixed for this request."
                                    : isSwap
                                        ? `Today or up to ${MAX_SWAP_AHEAD_DAYS} days ahead.`
                                        : `Today or up to ${MAX_BACKDATE_DAYS} days back.`}
                            </p>
                        </div>
                        <div className="min-w-0">
                            <label className={modalFieldLabelClass}>Your current shift</label>
                            <div className="mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 py-2 min-h-10">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {yourShiftParts.name || "Your assigned shift"}
                                </p>
                                {yourShiftParts.hint ? (
                                    <p className="text-[11px] text-muted-foreground truncate">{yourShiftParts.hint}</p>
                                ) : null}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1 leading-4 min-h-4">Taken from your roster for this date.</p>
                        </div>
                        <div className="min-w-0">
                            {isSwap ? (
                                <>
                                    <label className={modalFieldLabelClass}>Swap with</label>
                                    {typeLocked ? (
                                        <div className="mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-2 h-10 flex items-center overflow-hidden">
                                            {selectedCandidate ? (
                                                <EmployeeDetails
                                                    details={toEmployeeDetails(selectedCandidate.employee_details, selectedCandidate)}
                                                    size={"xs"}
                                                />
                                            ) : (
                                                <p className="text-sm text-muted-foreground">No colleague selected</p>
                                            )}
                                        </div>
                                    ) : (
                                        <Select
                                            className={cn(antSelectClass(), "mt-1")}
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Select a colleague"
                                            options={candidateOptions}
                                            value={form.counterpart_id || undefined}
                                            onChange={(v) => setForm((f) => ({ ...f, counterpart_id: v }))}
                                            disabled={loading}
                                            notFoundContent="No one on a different shift that day"
                                            listHeight={280}
                                            popupMatchSelectWidth={360}
                                            optionRender={(option) => {
                                                const person = displayCandidates.find((row) => String(row.id) === String(option.value));
                                                if (!person) return option.label;
                                                return (
                                                    <div className="py-1">
                                                        <EmployeeDetails
                                                            details={toEmployeeDetails(person.employee_details, person)}
                                                            isFromSubordinate
                                                        />
                                                        <p className="text-[11px] text-muted-foreground ml-10 truncate">
                                                            {buildShiftOptionLabel(person)}
                                                        </p>
                                                    </div>
                                                );
                                            }}
                                        />
                                    )}
                                    <p className="text-[11px] text-muted-foreground mt-2 leading-4 min-h-4 truncate">
                                        {selectedCandidate
                                            ? `Works ${theirShiftLabel}`
                                            : "Only colleagues on a different shift that day."}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <label className={modalFieldLabelClass}>Requested shift</label>
                                    <Select
                                        className={cn(antSelectClass(), "mt-1")}
                                        placeholder="Select the shift you should have worked"
                                        options={shiftOptions}
                                        value={form.requested_shift_id || undefined}
                                        onChange={(v) => setForm((f) => ({ ...f, requested_shift_id: v }))}
                                        disabled={loading || !hasAttendance}
                                    />
                                    <p className="text-[11px] text-muted-foreground mt-2 leading-4 min-h-4">
                                        Choose the shift that should have been assigned.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>

                    {isSwap ? (
                        <div className="grid grid-cols-2 gap-4">
                            <SwapPair
                                title="You"
                                fromLabel="You work"
                                fromName={yourShiftLabel}
                                toLabel="You take"
                                toName={selectedCandidate ? theirShiftLabel : "Waiting for colleague"}
                                ready={Boolean(selectedCandidate)}
                            />
                            <SwapPair
                                title="Colleague"
                                fromLabel="They work"
                                fromName={selectedCandidate ? theirShiftLabel : "Select a colleague"}
                                toLabel="They take"
                                toName={selectedCandidate ? yourShiftLabel : "Your shift"}
                                ready={Boolean(selectedCandidate)}
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                            <ShiftLane label="Currently" name={yourShiftLabel} />
                            <ArrowLeftRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            <ShiftLane
                                label="Change to"
                                name={form.requested_shift_id ? requestedShiftLabel : "Select a shift"}
                                accent={Boolean(form.requested_shift_id)}
                                empty={!form.requested_shift_id}
                            />
                        </div>
                    )}

                    <div>
                        <label className={modalFieldLabelClass}>Reason</label>
                        <Textarea
                            value={form.reason}
                            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                            rows={2}
                            maxLength={500}
                            showCharCount
                            className={cn(modalTextareaClass, "mt-1 h-20")}
                            disabled={loading || (!isSwap && !hasAttendance)}
                            placeholder={isSwap
                                ? "Why do you need to swap for this date? (min 10 characters)"
                                : "Explain why the assigned shift was incorrect (min 10 characters)"}
                        />
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={cancelOpen}
                onClose={() => setCancelOpen(false)}
                onConfirm={handleCancelRequest}
                title={isSwap ? "Cancel shift swap request?" : "Cancel shift change request?"}
                description={`This will cancel your pending request for ${form.date ? dayjs(form.date).format("DD MMM YYYY") : "this date"}.`}
                type="danger"
                confirmLabel="Cancel request"
                cancelLabel="Keep"
                loading={loading}
                destructive
            />
        </>
    );
};

export default ShiftChangeRequestDialog;
